'use client'

import Link from 'next/link'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import type { LimitModalDetails } from '@/hooks/use-usage-limit-handler'

interface UsageLimitModalProps {
  open: boolean
  modal: LimitModalDetails | null
  onOpenChange: (open: boolean) => void
}

function formatCredits(value: number) {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
  }).format(value)
}

function formatEventType(eventType: string) {
  return eventType
    .split('.')
    .filter(Boolean)
    .join(' ')
}

function formatPlan(plan: string) {
  if (!plan) return 'a higher plan'
  return plan.charAt(0).toUpperCase() + plan.slice(1)
}

export function UsageLimitModal({ open, modal, onOpenChange }: UsageLimitModalProps) {
  const isPlanRequired = modal?.kind === 'plan_required'
  const eventLabel = modal ? formatEventType(modal.details.eventType) : 'this action'
  const upgradeUrl = modal?.details.upgradeUrl
    || (isPlanRequired ? '/wallet?action=upgrade' : '/wallet?action=topup')
  const primaryCtaLabel = isPlanRequired ? 'Upgrade plan' : 'Top up credits'

  const title = isPlanRequired
    ? 'Upgrade required for this action'
    : "You've hit your credit limit"

  let description: React.ReactNode
  if (modal?.kind === 'usage_limit_exceeded') {
    const creditsRequired = formatCredits(modal.details.creditsRequired)
    const creditsAvailable = formatCredits(modal.details.creditsAvailable)
    description = (
      <>
        {eventLabel} needs {creditsRequired} credits, but your workspace has {creditsAvailable} available.
        Add credits to keep going, or review your usage first.
      </>
    )
  } else if (modal?.kind === 'plan_required') {
    const required = formatPlan(modal.details.requiredPlan)
    const current = formatPlan(modal.details.currentPlan)
    description = (
      <>
        {eventLabel} requires the {required} plan or higher (your workspace is on {current}).
        Upgrade your plan to enable this action.
      </>
    )
  } else {
    description = 'Add credits to keep going, or review your usage first.'
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-atrium-border bg-atrium-inner font-sans sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold tracking-[-0.02em] text-atrium-black">
            {title}
          </DialogTitle>
          <DialogDescription className="text-sm leading-6 text-atrium-muted">
            {description}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            className="text-atrium-muted hover:text-atrium-black"
            onClick={() => onOpenChange(false)}
          >
            Dismiss
          </Button>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button asChild variant="outline">
              <Link href="/wallet" onClick={() => onOpenChange(false)}>
                View usage
              </Link>
            </Button>
            <Button asChild variant="atrium">
              <Link href={upgradeUrl} onClick={() => onOpenChange(false)}>
                {primaryCtaLabel}
              </Link>
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
